import { useMemo, useState } from "react";
import {
  ArrowRightLeft,
  Bell,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  LayoutDashboard,
  LogOut,
  Search,
  Settings,
  UserRound,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

const FALLBACK_USER = {
  name: "Marc Dupuis",
  email: "marc.dupuis@fc-maquette.fr",
  clubName: "FC MAQUETTE",
};

const SIDEBAR_ITEM =
  "flex w-full min-h-[39px] items-center gap-[11px] rounded-md border-0 bg-transparent px-[11px] text-left text-[11px] text-[#9792c7] no-underline hover:bg-[rgba(98,94,166,0.17)] hover:text-v2-foreground";

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function normalizeUser(me, t) {
  const user = me?.user || me?.account || me || {};
  const club = me?.club || me?.club_info || user?.club || {};
  const firstName = firstValue(user.first_name, user.firstname);
  const lastName = firstValue(user.last_name, user.lastname);
  const name =
    firstValue(
      user.full_name,
      user.name,
      [firstName, lastName].filter(Boolean).join(" "),
      user.username,
      user.login
    ) || FALLBACK_USER.name;
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return {
    name,
    initials: initials || "MD",
    email: firstValue(user.email, user.mail) || FALLBACK_USER.email,
    role: firstValue(user.role, user.role_name, user.roles?.[0]) || t("v2.shell.fallback_role"),
    clubName: firstValue(club.name, club.club_name, me?.club_name) || FALLBACK_USER.clubName,
    clubTeam: firstValue(club.team_name, me?.team_name) || t("v2.shell.fallback_club_team"),
    clubLogo: firstValue(club.logo, club.logo_url, me?.club_logo),
  };
}

function UserAvatar({ user, large = false }) {
  return (
    <span
      className={`grid shrink-0 place-items-center rounded-full bg-[#2d287e] font-bold text-[#d8d5ff] ${
        large ? "h-[43px] w-[43px] text-[14px]" : "h-7 w-7 text-[10px]"
      }`}
      aria-hidden="true"
    >
      {user.initials}
    </span>
  );
}

function ClubMark({ user }) {
  return user.clubLogo ? (
    <img className="grid h-8 w-8 shrink-0 place-items-center object-contain" src={user.clubLogo} alt="" />
  ) : (
    <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-[#f2f1ff]" aria-hidden="true">
      <img className="h-[27px] w-[27px] object-contain" src="/icon.png" alt="" />
    </span>
  );
}

function Sidebar({ collapsed, onToggle, onLogout, user }) {
  const { t } = useTranslation();
  const location = useLocation();
  const active = location.pathname === "/v2/overview";
  const toggleLabel = collapsed ? t("v2.shell.expand_menu") : t("v2.shell.collapse_menu");
  const itemPad = collapsed ? "justify-center px-0" : "";

  return (
    <aside
      className={`flex flex-col border-r border-[rgba(98,94,166,0.25)] bg-[#08032d] py-[15px] transition-[width,flex-basis] duration-200 ease-in ${
        collapsed
          ? "w-[68px] shrink-0 basis-[68px] items-center px-[9px]"
          : "w-[218px] shrink-0 basis-[218px] px-2.5"
      }`}
    >
      <div
        className={`flex min-h-[46px] w-full items-center gap-[9px] ${collapsed ? "justify-center p-0" : "px-[7px]"}`}
        title={collapsed ? user.clubName : undefined}
      >
        <ClubMark user={user} />
        {!collapsed && (
          <div className="flex min-w-0 flex-col">
            <strong className="overflow-hidden text-ellipsis whitespace-nowrap text-[10px] tracking-[0.04em] text-[#f3f2ff]">
              {user.clubName}
            </strong>
            <span className="text-[9px] text-v2-subtle">{user.clubTeam}</span>
          </div>
        )}
      </div>

      <div className="my-2.5 mb-3.5 h-px w-full bg-[rgba(98,94,166,0.28)]" />

      <nav className="grid w-full gap-[5px]" aria-label={t("v2.shell.main_nav")}>
        <Link
          to="/v2/overview"
          className={`${SIDEBAR_ITEM} ${itemPad} ${
            active ? "bg-[rgba(0,48,254,0.26)] text-[#f7f5ff] shadow-[inset_2px_0_0_#34cbff]" : ""
          }`}
          aria-current={active ? "page" : undefined}
          title={collapsed ? t("v2.shell.team_overview") : undefined}
        >
          <LayoutDashboard size={18} strokeWidth={1.8} />
          {!collapsed && <span>{t("v2.shell.team_overview")}</span>}
        </Link>
      </nav>

      <div className="mt-auto grid w-full gap-1">
        <button
          type="button"
          className={`${SIDEBAR_ITEM} ${itemPad}`}
          onClick={onToggle}
          title={toggleLabel}
          aria-label={toggleLabel}
        >
          {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          {!collapsed && <span>{t("v2.shell.collapse_menu")}</span>}
        </button>
        <Link
          to="/"
          className={`${SIDEBAR_ITEM} ${itemPad} text-[#8edbff]`}
          title={t("v2.shell.legacy_interface_title")}
          aria-label={t("v2.shell.legacy_interface_title")}
        >
          <ArrowRightLeft size={18} strokeWidth={1.8} />
          {!collapsed && <span>{t("v2.shell.legacy_interface")}</span>}
        </Link>
        <button
          type="button"
          className={`${SIDEBAR_ITEM} ${itemPad} text-[#e36579]`}
          onClick={onLogout}
          title={t("v2.common.logout")}
          aria-label={t("v2.common.logout")}
        >
          <LogOut size={18} strokeWidth={1.8} />
          {!collapsed && <span>{t("v2.common.logout")}</span>}
        </button>
      </div>
    </aside>
  );
}

function ProfileMenu({ user, alertsEnabled, onAlertsChange, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const menuBtn =
    "flex w-full min-h-[42px] items-center gap-3 border-0 bg-transparent px-4 text-left text-[12px] text-[#f1efff] hover:bg-[rgba(98,94,166,0.18)]";

  return (
    <div
      className="absolute right-0 top-[calc(100%+10px)] w-80 overflow-hidden rounded-xl border border-[#45409b] bg-[#1b1754] shadow-v2-pop"
      role="menu"
    >
      <div className="flex items-center gap-3 px-4 pb-[11px] pt-[17px]">
        <UserAvatar user={user} large />
        <div className="flex min-w-0 flex-col">
          <strong className="text-[14px] font-semibold">{user.name}</strong>
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-v2-muted">{user.email}</span>
        </div>
      </div>

      <div className="mb-3 ml-4 mr-4 inline-flex items-center gap-[5px] rounded-[20px] border border-[#635eb2] px-[9px] py-1 text-[10px] text-[#ddd9ff]">
        <ShieldIcon />
        {user.role}
      </div>

      <div className="mx-4 mb-4 flex items-center gap-[11px] rounded-[7px] border border-[rgba(98,94,166,0.4)] bg-[rgba(9,4,49,0.36)] p-2.5">
        <ClubMark user={user} />
        <div className="flex min-w-0 flex-col">
          <strong className="text-[12px] tracking-[0.02em]">{user.clubName}</strong>
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-v2-muted">{user.clubTeam}</span>
        </div>
      </div>

      <div className="border-y border-[rgba(98,94,166,0.34)] py-[5px]">
        <button type="button" className={menuBtn} onClick={() => navigate("/v2/profile")} role="menuitem">
          <UserRound size={17} className="text-[#a9a4dc]" />
          {t("v2.shell.profile")}
        </button>
        <button type="button" className={menuBtn} onClick={() => navigate("/v2/settings")} role="menuitem">
          <Settings size={17} className="text-[#a9a4dc]" />
          {t("v2.shell.settings")}
        </button>
        <label className="v2-alert-setting flex min-h-[42px] w-full cursor-pointer items-center justify-between gap-3 px-4 text-[12px] text-[#f1efff]">
          <span className="flex items-center gap-3">
            <Bell size={17} className="text-[#a9a4dc]" />
            {t("v2.shell.overload_alerts")}
          </span>
          <input
            type="checkbox"
            className="pointer-events-none absolute opacity-0"
            checked={alertsEnabled}
            onChange={(event) => onAlertsChange(event.target.checked)}
          />
          <span className="v2-toggle" aria-hidden="true" />
        </label>
      </div>

      <button
        type="button"
        className="flex min-h-12 w-full items-center gap-3 border-0 bg-transparent px-4 text-left text-[12px] text-[#ff7481] hover:bg-[rgba(255,77,94,0.1)]"
        onClick={onLogout}
        role="menuitem"
      >
        <LogOut size={17} />
        {t("v2.common.logout")}
      </button>
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
      <path
        d="M12 3.5 19 6v5.2c0 4.4-2.8 7.8-7 9.3-4.2-1.5-7-4.9-7-9.3V6l7-2.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

export default function V2Shell({ children, me, onLogout }) {
  const { t, i18n } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(
    () => window.localStorage.getItem("v2_overload_alerts") !== "false"
  );
  const language = (i18n.language || "fr").slice(0, 2);
  const user = useMemo(() => normalizeUser(me, t), [me, t]);

  const changeAlerts = (enabled) => {
    setAlertsEnabled(enabled);
    window.localStorage.setItem("v2_overload_alerts", String(enabled));
  };

  return (
    <div
      className="v2-root flex h-full min-h-screen min-w-[1000px] w-full flex-col overflow-hidden bg-v2-background font-v2 text-[14px] leading-[1.55] text-v2-foreground"
      lang={language}
    >
      <header className="relative z-20 flex min-h-16 w-full shrink-0 items-center justify-between border-b border-[rgba(98,94,166,0.28)] bg-[#08032d] px-6 max-[900px]:px-[13px] max-[650px]:min-h-14">
        <Link
          to="/v2/overview"
          className="flex shrink-0 basis-[235px] items-center text-inherit no-underline max-[900px]:basis-auto"
          aria-label={t("v2.brand.monitoring")}
        >
          <img
            className="block h-auto max-h-7 w-[180px] object-contain object-left max-[900px]:h-7 max-[900px]:w-7 max-[900px]:object-cover"
            src="/logo.png"
            alt={t("v2.brand.monitoring")}
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = "/logo-light.png";
            }}
          />
        </Link>

        <label className="mx-6 flex h-[34px] max-w-[430px] flex-1 basis-[420px] items-center gap-[9px] rounded-[5px] border border-[rgba(98,94,166,0.48)] bg-[rgba(17,12,61,0.6)] px-[11px] text-v2-subtle focus-within:border-v2-cyan focus-within:shadow-[0_0_0_2px_rgba(52,203,255,0.12)] max-[900px]:mx-3 max-[900px]:max-w-none">
          <Search size={15} aria-hidden="true" />
          <input
            type="search"
            className="w-full min-w-0 border-0 bg-transparent text-[10px] text-v2-foreground outline-none placeholder:text-[#7772ad]"
            placeholder={t("v2.shell.search_placeholder")}
          />
        </label>

        <div className="flex shrink-0 items-center gap-2.5">
          <button
            type="button"
            className="relative grid h-[29px] w-[29px] place-items-center rounded-[5px] border-0 bg-transparent text-v2-muted hover:bg-v2-accent hover:text-v2-foreground"
            aria-label={t("v2.shell.notifications")}
          >
            <Bell size={17} strokeWidth={1.7} />
            <span className="absolute -right-[3px] -top-[3px] grid h-3.5 min-w-[14px] place-items-center rounded-[10px] bg-v2-bad text-[8px] font-bold text-white">
              3
            </span>
          </button>
          <LanguageSwitcher size="topbar" />
          <div className="relative">
            <button
              type="button"
              className={`flex min-w-[166px] items-center gap-2 rounded-[7px] border border-transparent px-2 py-[5px] text-left text-v2-foreground max-[900px]:min-w-0 ${
                profileOpen ? "border-[rgba(98,94,166,0.65)] bg-[rgba(35,30,98,0.65)]" : "hover:border-[rgba(98,94,166,0.65)] hover:bg-[rgba(35,30,98,0.65)]"
              }`}
              onClick={() => setProfileOpen((open) => !open)}
              aria-expanded={profileOpen}
              aria-haspopup="menu"
            >
              <UserAvatar user={user} />
              <span className="flex min-w-0 flex-1 flex-col leading-[1.2] max-[900px]:hidden">
                <strong className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-semibold">
                  {user.name}
                </strong>
                <small className="overflow-hidden text-ellipsis whitespace-nowrap text-[9px] text-v2-subtle">
                  {user.role}
                </small>
              </span>
              <ChevronDown size={15} className="text-v2-subtle" />
            </button>
            {profileOpen && (
              <ProfileMenu
                user={user}
                alertsEnabled={alertsEnabled}
                onAlertsChange={changeAlerts}
                onLogout={onLogout}
              />
            )}
          </div>
        </div>
      </header>

      <div className="flex min-h-0 min-w-0 flex-1">
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed((value) => !value)}
          onLogout={onLogout}
          user={user}
        />
        <main className="v2-page-content min-w-0 flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
